import React, { PropsWithChildren, ReactNode } from 'react';
import { Form, Formik } from 'formik';
import { ProfileUser, Retailer } from '@ordermentum/ui-types';
import classNames from 'classnames';
import { EXTERNAL_LINKS } from '@ordermentum/ui-constants';
import { toast } from '@ordermentum/ui-shared/components/Toast/Toast';
import Button from '../../Button/Button';
import Banner from '../../Banner';
import Divider from '../../Divider';
import { Checkbox } from '../../Input/Checkbox';
import { Input } from '../../../atoms/Input';
import { directDebitFormSchema } from '../../../utilities/formSchemas';
import PurchaserToggleBlock from './PurchaserToggleBlock';
import { DirectDebitParams } from '../../../redux/payment_methods';
import BSBInput from './BSBInput';
import DirectDebitAgreement from './DirectDebitAgreement';

const CTA_POSITIONS = {
  FIXED: 'fixed',
  STICKY: 'sticky',
};

type DirectDebitRequestType = PropsWithChildren<{
  user?: ProfileUser;
  isPaymentLink?: boolean;
  retailer?: Retailer;
  retailerId: string;
  title?: string;
  CTAPosition?: 'fixed' | 'sticky';
  hideCancel?: boolean;
  purchaserId?: string;
  onInviteSignatoryClick?: () => void;
  addDirectPayment: (
    directDetails: DirectDebitParams,
    retailerId: string
  ) => Promise<{ value: { data: { id: string } } } | void> | void;
  onSuccess?: (paymentMethodId: string) => void;
  onCancel?: () => void;
}>;

type SectionType = {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
};

const Section = (props: SectionType) => {
  const { title, subtitle, children } = props;

  return (
    <section aria-labelledby={title} className="flex flex-col gap-l">
      <div className="flex flex-col gap-xxs">
        <h4 className="font-heading text-h4 text-strong">{title}</h4>
        {subtitle && (
          <p className="font-body text-body-small text-regular">{subtitle}</p>
        )}
      </div>
      <div>{children}</div>
    </section>
  );
};

type FieldInformationType = {
  label: string;
  text?: string;
};

const FieldInformation = (props: FieldInformationType) => {
  const { label, text } = props;
  return (
    <div className="flex max-w-72 grow flex-col gap-xxs tablet-min:w-full">
      <label className="font-body text-label-small text-strong">{label}</label>
      <p className="line-clamp-2 text-ellipsis font-body  text-body text-regular">
        {text || '-'}
      </p>
    </div>
  );
};

const InputRow = ({ children }: { children: ReactNode }) => (
  <div className="flex grow flex-col gap-xxs">{children}</div>
);

type FormValues = {
  acknowledgement: boolean;
  firstSignatory: string;
  accountName: string;
  bsb: string;
  accountNumber: string;
  defaultAll: boolean;
  email: string;
  phone: string;
};

/**
 * Validates additional requirements not covered by Formik schema
 * @param formValues
 * @param retailerAbn
 */
function validateAdditionalRequirements(
  formValues: FormValues,
  retailerAbn: string
) {
  if (!retailerAbn) {
    toast.error(
      "You're missing some required business information. Ensure you have an ABN attached to your business."
    );
    return false;
  }

  if (!formValues.phone) {
    toast.error(
      'Your profile is missing some required information. Ensure you have a phone number attached to your profile.'
    );
    return false;
  }

  return true;
}

export const DirectDebitRequest = (props: DirectDebitRequestType) => {
  const {
    user,
    retailer,
    retailerId,
    CTAPosition = CTA_POSITIONS.FIXED,
    hideCancel = false,
    isPaymentLink = false,
    title,
    purchaserId,
    addDirectPayment,
    onCancel,
    onInviteSignatoryClick,
    onSuccess,
  } = props;

  // user and retailer can be undefined (ex: payment link)
  const tradingName = retailer?.tradingName;
  const address = retailer?.billingAddress?.formatted;
  const abn = retailer?.abn;
  const fullName = user?.fullName;
  const email = user?.email;
  const phone = user?.phone;

  const handleSubmit = async (values: FormValues, actions) => {
    if (!validateAdditionalRequirements(values, abn || '')) {
      return;
    }

    const payload = {
      account: values.accountNumber,
      accountName: values.accountName,
      bsb: values.bsb,
      country: 'AUS',
      currency: 'AUD',
      defaultPayment: true,
      defaultAll: values.defaultAll,
      type: 'bank',
      terms: values.acknowledgement,
      userId: retailerId,
      purchaserId,
    };

    try {
      const res = await addDirectPayment(payload, retailerId);
      // onSuccess may unmount this component, so we should clean up first
      actions.resetForm();
      if (onSuccess) {
        // @ts-expect-error the action does return the response
        onSuccess(res.value.data.id, {
          name: values.firstSignatory,
          email: values.email,
          phone: values.phone,
          retailerId,
        });
      }
    } catch (error) {
      actions.setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-l">
      {title && <h2 className="font-heading text-h2 text-strong">{title}</h2>}
      {onInviteSignatoryClick && (
        <Banner
          variant="info"
          title="Set up your bank account to pay invoices."
          actions={{
            alignment: 'start',
            items: [
              <Button
                key="invite-signatory"
                variant="text-link"
                expandOnMobile={false}
                onClick={onInviteSignatoryClick}
              >
                Invite authorised signatory
              </Button>,
            ],
          }}
        >
          Direct debit request must be submitted by an authorised signatory of
          your business bank account. If this is not you, please invite them to
          submit this request.
        </Banner>
      )}
      <Formik
        validationSchema={directDebitFormSchema}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          editable: isPaymentLink,
          firstSignatory: user?.fullName ?? '',
          accountName: '',
          bsb: '',
          accountNumber: '',
          acknowledgement: false,
          email: user?.email ?? '',
          phone: user?.phone ?? '',
          defaultAll: isPaymentLink,
        }}
        onSubmit={handleSubmit}
      >
        {({ values, errors, setFieldValue, isSubmitting, resetForm }) => (
          <Form className="flex flex-col gap-l">
            <Section
              title="Business details"
              subtitle={
                !isPaymentLink
                  ? 'If you would like to make changes to these details, please go to Business Details in Settings.'
                  : ''
              }
            >
              <div className="flex gap-m">
                <FieldInformation label="Business name" text={tradingName} />
                <FieldInformation label="Billing address" text={address} />
                <FieldInformation label="ABN" text={abn} />
              </div>
            </Section>
            <Divider type="subtle" />
            <Section title="Account details">
              <div className="flex gap-m tablet-min:flex-col tablet-max:flex-wrap">
                <InputRow>
                  <Input
                    isFuturo
                    tag="accountName"
                    inputLabel="Account name"
                    hasError={!!errors.accountName}
                    errorLabel={errors.accountName}
                    size="max"
                    value={values.accountName}
                    onChange={setFieldValue}
                    onBlur={setFieldValue}
                  />
                </InputRow>
                <InputRow>
                  <BSBInput
                    value={values.bsb}
                    error={errors.bsb}
                    onChange={setFieldValue}
                  />
                </InputRow>
                <InputRow>
                  <Input
                    isFuturo
                    tag="accountNumber"
                    inputLabel="Account number"
                    hasError={!!errors.accountNumber}
                    errorLabel={errors.accountNumber}
                    size="max"
                    value={values.accountNumber}
                    onChange={setFieldValue}
                    onBlur={setFieldValue}
                  />
                </InputRow>
              </div>
            </Section>
            <Divider type="subtle" />
            <Section
              title="Contact details"
              subtitle={
                !isPaymentLink
                  ? 'If you would like to make changes to these details, please go to User Details in Settings.'
                  : ''
              }
            >
              <div
                className={classNames('flex gap-xs', {
                  'flex-col': isPaymentLink,
                })}
              >
                {isPaymentLink ? (
                  <>
                    <InputRow>
                      <Input
                        isFuturo
                        tag="firstSignatory"
                        inputLabel="Authorised signatory"
                        hasError={!!errors.firstSignatory}
                        errorLabel={errors.firstSignatory}
                        size="max"
                        value={values.firstSignatory}
                        onChange={setFieldValue}
                        onBlur={setFieldValue}
                      />
                    </InputRow>
                    <div className="flex gap-m">
                      <InputRow>
                        <Input
                          isFuturo
                          tag="email"
                          inputLabel="Email"
                          hasError={!!errors.email}
                          errorLabel={errors.email}
                          size="max"
                          value={values.email}
                          onChange={setFieldValue}
                          onBlur={setFieldValue}
                        />
                      </InputRow>
                      <InputRow>
                        <Input
                          isFuturo
                          tag="phone"
                          inputLabel="Phone"
                          hasError={!!errors.phone}
                          errorLabel={errors.phone}
                          size="max"
                          value={values.phone}
                          onChange={setFieldValue}
                          onBlur={setFieldValue}
                        />
                      </InputRow>
                    </div>
                  </>
                ) : (
                  <>
                    <FieldInformation
                      label="Authorised signatory"
                      text={fullName}
                    />
                    <FieldInformation label="Email" text={email} />
                    <FieldInformation label="Phone" text={phone} />
                  </>
                )}
              </div>
            </Section>
            <Divider type="subtle" />
            {!isPaymentLink && (
              <PurchaserToggleBlock
                type="direct"
                allowToggleAll
                formikFunction={checked => {
                  setFieldValue('defaultAll', checked);
                }}
                formikValue={values.defaultAll}
              />
            )}
            <Checkbox
              name="acknowledgement"
              onChange={val => {
                setFieldValue('acknowledgement', val);
              }}
            >
              <div className="flex flex-col">
                <p className="text-justify text-body text-regular">
                  I, {fullName ?? '[user name]'}, by checking this box, accept
                  the&nbsp;
                  <Button
                    as="a"
                    expandOnMobile={false}
                    variant="text-link"
                    className="p-0"
                    href={EXTERNAL_LINKS.LEGAL.DIRECT_DEBIT_TERMS}
                  >
                    Ordermentum Direct Debit Agreement.
                  </Button>
                  &nbsp;I also confirm that:
                </p>
                <DirectDebitAgreement />
              </div>
            </Checkbox>

            <div
              className={classNames({
                'bg-regular fixed tablet-max:w-full tablet-max:max-w-full tablet-max:left-0 max-w-screen-md w-1/2 transition-bottom duration-1000':
                  CTAPosition === CTA_POSITIONS.STICKY,
                'flex flex-col gap-l': CTAPosition === CTA_POSITIONS.FIXED,
                'bottom-0': values.acknowledgement,
                '-bottom-44': !values.acknowledgement,
              })}
            >
              <Divider type="subtle" />
              <div
                className={classNames(
                  'flex justify-end gap-xxs tablet-max:flex-col-reverse',
                  {
                    'pt-xl pb-xxxl px-xl': CTAPosition === CTA_POSITIONS.STICKY,
                  }
                )}
              >
                {!hideCancel && (
                  <Button
                    variant="secondary"
                    type="reset"
                    onClick={() => {
                      resetForm();

                      if (onCancel) {
                        onCancel();
                      }
                    }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!values.acknowledgement}
                  loading={isSubmitting}
                >
                  Submit
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default DirectDebitRequest;
